import React, {
  CSSProperties,
  ImgHTMLAttributes,
  memo,
  useEffect,
  useRef,
  useState
} from "react";

export const imagePlaceholderColors = {
  DEFAULT: "#F6F6F6",
  PLP: "#F6F6F6",
  PDP: "#FFF8F3",
  PRODUCT_CAROUSEL: "#E0F7FA",
  GENERIC_CAROUSEL: "#FFF7D8",
  SINGLE_BANNER: "#D9E3F0",
  SLIDER: "#FFFCED"
} as const;

export type ImagePlaceholderType = keyof typeof imagePlaceholderColors;

export type ImgProps = {
  src?: string;
  placeholderImageSrc?: string;
  alt?: string;
  className?: string;
  imagePlaceholderType?: ImagePlaceholderType;
  onLoadEnd?: () => void;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  style?: CSSProperties;
  objectFit?: CSSProperties["objectFit"];
} & Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "className" | "style" | "onLoad" | "onError"
>;

const ImgInner = ({
  src,
  placeholderImageSrc,
  alt = "",
  className,
  imagePlaceholderType = "DEFAULT",
  onLoadEnd,
  onLoad,
  onError,
  style,
  objectFit = "cover",
  ...imgProps
}: ImgProps) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const loadFiredRef = useRef(false);

  useEffect(() => {
    loadFiredRef.current = false;
    setLoaded(false);
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setLoaded(true);
      loadFiredRef.current = true;
      onLoadEnd?.();
    }
  }, [src]);

  if (!src) return null;

  const color = imagePlaceholderColors[imagePlaceholderType];
  const hasThumb = Boolean(placeholderImageSrc);
  const placeholderColor = loaded ? "transparent" : color;

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = e => {
    setLoaded(true);
    if (!loadFiredRef.current) {
      loadFiredRef.current = true;
      onLoadEnd?.();
    }
    onLoad?.(e);
  };

  const handleError: React.ReactEventHandler<HTMLImageElement> = e => {
    if (!loadFiredRef.current) {
      loadFiredRef.current = true;
      onLoadEnd?.();
    }
    onError?.(e);
  };

  if (!hasThumb) {
    return (
      <img
        {...imgProps}
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        style={{ backgroundColor: placeholderColor, ...style }}
        onLoad={handleLoad}
        onError={handleError}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        ...style,
        position: "relative",
        backgroundColor: placeholderColor
      }}
    >
      {!loaded && (
        <img
          src={placeholderImageSrc}
          alt=""
          aria-hidden={true}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit,
            pointerEvents: "none"
          }}
        />
      )}

      <img
        {...imgProps}
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          display: "block",
          width: "100%",
          objectFit,
          opacity: 1
        }}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

export const Img = memo(ImgInner);
export default Img;
